import React, { useCallback, useEffect, useState } from 'react';
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaShieldAlt, FaTachometerAlt } from 'react-icons/fa';
import { useAuth } from '../auth/AuthContext';
import { useVehicle } from '../contexts/VehicleContext';
import { usedListingService } from '../services/apiService';
import { freshnessService } from '../services/freshnessService';
import { getGradeMeta } from '../utils/usedPartGrades';
import { ProductDetailSkeleton } from './Skeleton';
import FitmentStrip from './partDetail/FitmentStrip';
import PriceBreakdownCard from './partDetail/PriceBreakdownCard';
import SellerBlock from './partDetail/SellerBlock';
import UsedPhotoGallery from './partDetail/used/UsedPhotoGallery';
import UsedCtaSection from './partDetail/used/UsedCtaSection';
import UsedVerificationPhotoSection from './partDetail/used/UsedVerificationPhotoSection';
import CrossSellSection from './partDetail/CrossSellSection';
import { useCrossSell } from '../hooks/useCrossSell';
import { useThemeClasses } from '../hooks/useThemeClasses';

function ConfirmModal({ title, body, confirmLabel, onConfirm, onCancel, loading }) {
  return (
    <div className="fixed inset-0 z-[125] flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-600 bg-[#0f172a] p-6 shadow-2xl">
        <h3 className="text-lg font-bold text-white">{title}</h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-300">{body}</p>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-slate-600 px-5 py-2.5 text-sm font-semibold text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-bold text-slate-900 hover:bg-amber-400 disabled:opacity-50"
          >
            {loading ? 'Please wait…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * B-05 — Part Detail (Used Parts)
 */
export default function PartDetailUsed({ listingIdOverride } = {}) {
  const { itemId } = useParams();
  const listingId = listingIdOverride || itemId;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { selectedVehicle } = useVehicle();
  const { isDark, sc } = useThemeClasses();

  const resolvedVehicleId =
    searchParams.get('vehicleId') ||
    selectedVehicle?._id ||
    selectedVehicle?.id ||
    null;

  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState(null);
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(false);
  const [expressLoading, setExpressLoading] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [showCheckModal, setShowCheckModal] = useState(false);
  const [showExpressModal, setShowExpressModal] = useState(false);
  const [paying, setPaying] = useState(false);

  const loadListing = useCallback(async () => {
    if (!listingId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await usedListingService.getListing(listingId, {
        vehicleId: resolvedVehicleId,
      });
      setListing(data);
      if (data?.availabilityRequestSent) {
        setRequestSent(true);
      }
    } catch (err) {
      setError(err.status === 404 ? 'unavailable' : err.message || 'Failed to load listing');
    } finally {
      setLoading(false);
    }
  }, [listingId, resolvedVehicleId]);

  useEffect(() => {
    loadListing();
  }, [loadListing]);

  useEffect(() => {
    if (!listing?.listingId) return;
    freshnessService.recordView(listing.listingId).catch(() => {});
  }, [listing?.listingId]);

  useEffect(() => {
    if (!requestSent || listing?.verificationPhoto?.url) return undefined;
    const id = setInterval(loadListing, 12000);
    return () => clearInterval(id);
  }, [requestSent, listing?.verificationPhoto?.url, loadListing]);

  const crossSellNodeId = listing?.catalogNodeId || listing?.nodeId;
  const crossSell = useCrossSell(crossSellNodeId, listingId, 12);

  const requireAuth = () => {
    navigate('/login', {
      state: { from: location.pathname + location.search },
    });
    return false;
  };

  const submitCheckAvailability = async () => {
    if (!isAuthenticated && !requireAuth()) return;
    setChecking(true);
    try {
      await usedListingService.checkAvailability({
        listingId: listing.listingId,
        vehicleId: resolvedVehicleId,
      });
      setShowCheckModal(false);
      setRequestSent(true);
      toast.success('Request sent to seller.');
    } catch (err) {
      toast.error(err.message || 'Failed to send request.');
    } finally {
      setChecking(false);
    }
  };

  const submitExpressBuy = async () => {
    if (!isAuthenticated && !requireAuth()) return;
    setExpressLoading(true);
    try {
      const res = await usedListingService.expressBuy({
        listingId: listing.listingId,
        vehicleId: resolvedVehicleId,
      });
      const item = res.checkoutItem || res.data?.checkoutItem;
      const meta = res.usedPartMeta || res.data?.usedPartMeta;
      if (!item) throw new Error('Checkout session unavailable');

      localStorage.removeItem('buyNowItem');
      localStorage.setItem(
        'buyNowItem',
        JSON.stringify({
          ...item,
          usedPartMeta: meta,
        })
      );
      setShowExpressModal(false);
      navigate('/checkout/address');
    } catch (err) {
      toast.error(err.message || 'Express Buy failed.');
    } finally {
      setExpressLoading(false);
    }
  };

  const handlePayNowAfterPhoto = async () => {
    if (!isAuthenticated && !requireAuth()) return;
    const orderId = listing?.verificationPhoto?.orderId;
    if (!orderId) {
      toast.error('Order not found for this verification photo.');
      return;
    }
    setPaying(true);
    try {
      const res = await usedListingService.proceedAfterVerificationPhoto({
        orderId,
        listingId: listing.listingId,
      });
      const item = res.checkoutItem || res.data?.checkoutItem;
      const meta = res.usedPartMeta || res.data?.usedPartMeta;
      if (!item) throw new Error('Checkout session unavailable');

      localStorage.removeItem('buyNowItem');
      localStorage.setItem(
        'buyNowItem',
        JSON.stringify({
          ...item,
          usedPartMeta: meta,
        })
      );
      toast.success('Proceed to checkout to complete payment.');
      navigate('/checkout/address');
    } catch (err) {
      toast.error(err.message || 'Could not proceed to checkout.');
    } finally {
      setPaying(false);
    }
  };

  if (loading) return <ProductDetailSkeleton />;

  if (error === 'unavailable') {
    return (
      <div className={`${sc('pdpShell')} px-4 py-16 text-center`}>
        <p className="text-lg font-semibold">This listing is no longer available.</p>
        <button
          type="button"
          onClick={() => navigate('/catalog')}
          className="mt-6 rounded-xl bg-blue-600 px-6 py-2.5 font-bold text-white"
        >
          Browse alternatives
        </button>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className={`${sc('pdpShell')} flex items-center justify-center`}>
        <p className="text-slate-400">{error || 'Listing not found'}</p>
      </div>
    );
  }

  const gradeMeta = getGradeMeta(listing.grade);
  const minPhotos = listing.minPhotosRequired || gradeMeta.minPhotos;

  const ctaProps = {
    listing,
    requestSent: requestSent && !listing.verificationPhoto?.url,
    checking,
    expressLoading,
    onCheckAvailability: () => {
      if (!isAuthenticated) {
        requireAuth();
        return;
      }
      setShowCheckModal(true);
    },
    onExpressBuy: () => {
      if (!isAuthenticated) {
        requireAuth();
        return;
      }
      setShowExpressModal(true);
    },
  };

  return (
    <div className={`${sc('pdpShell')} pb-[calc(8.75rem+env(safe-area-inset-bottom,0px))] md:pb-8`}>
      <div className={`border-b ${isDark ? 'border-slate-800/80 bg-[#0b1120]' : 'border-slate-200 bg-white'}`}>
        <div className="mx-auto max-w-[1200px] px-3 py-2 text-xs sm:px-4 sm:text-sm">
          <nav className="flex flex-wrap items-center gap-1 text-slate-500">
            <button
              type="button"
              onClick={() => navigate('/catalog?spareType=used')}
              className={`font-medium hover:underline ${isDark ? 'text-[#8ab4f8]' : 'text-[#2874f0]'}`}
            >
              Used parts
            </button>
            <span className={isDark ? 'text-slate-600' : 'text-slate-400'}>/</span>
            <span className={`line-clamp-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {listing.name}
            </span>
          </nav>
        </div>
      </div>

      <div className="mx-auto max-w-[1200px] px-0 py-0 sm:px-3 sm:py-3 md:px-4">
        <div
          className={`overflow-hidden sm:rounded-sm sm:border ${
            isDark
              ? 'sm:border-slate-700/60 sm:bg-slate-900/40'
              : 'bg-white sm:border-slate-200 sm:shadow-sm'
          }`}
        >
          <div className="grid grid-cols-1 gap-0 md:grid-cols-12">
            <div className="md:col-span-5">
              <div className="md:sticky md:top-[72px]">
                <div className={isDark ? 'md:border-r md:border-slate-700/60' : 'md:border-r md:border-slate-100'}>
                  <UsedPhotoGallery
                    photos={listing.photos}
                    minRequired={minPhotos}
                    provenance={listing.provenance}
                  />
                  <div
                    className={`hidden border-t px-3 py-3 md:block ${
                      isDark ? 'border-slate-700/60' : 'border-slate-100'
                    }`}
                  >
                    {listing.verificationPhoto?.url ? (
                      <button
                        type="button"
                        onClick={handlePayNowAfterPhoto}
                        disabled={paying}
                        className={`${sc('pdpBuyNowBtn')} w-full`}
                      >
                        {paying ? 'Please wait…' : 'Pay Now'}
                      </button>
                    ) : (
                      <UsedCtaSection {...ctaProps} />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-7">
              <div className="flex min-h-0 flex-col gap-4 p-4 sm:p-5 md:gap-5 md:p-6 lg:px-8 lg:py-6">
                <FitmentStrip fitment={listing.fitment} compact />

                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex rounded-sm px-2 py-0.5 text-xs font-bold uppercase tracking-wide ${
                        isDark
                          ? 'border border-amber-500/40 bg-amber-500/15 text-amber-300'
                          : 'border border-amber-200 bg-amber-50 text-amber-800'
                      }`}
                    >
                      {listing.gradeLabel || gradeMeta.label}
                    </span>
                    {(listing.gradeKmCap || gradeMeta.kmCap) && (
                      <span
                        className={`rounded-sm px-2 py-0.5 text-xs font-medium ${
                          isDark
                            ? 'border border-slate-600 bg-slate-800/60 text-slate-300'
                            : 'border border-slate-200 bg-slate-50 text-slate-600'
                        }`}
                      >
                        {listing.gradeKmCap || gradeMeta.kmCap} km
                      </span>
                    )}
                  </div>
                  {(listing.gradeCondition || gradeMeta.condition) && (
                    <p className={`mt-2 text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                      {listing.gradeCondition || gradeMeta.condition}
                    </p>
                  )}
                  <h1
                    className={`mt-2 text-[16px] font-normal leading-snug sm:text-[18px] lg:text-[20px] ${
                      isDark ? 'text-slate-100' : 'text-slate-900'
                    }`}
                  >
                    {listing.name}
                  </h1>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    {listing.brand && (
                      <p className={`font-medium ${isDark ? 'text-[#8ab4f8]' : 'text-[#2874f0]'}`}>
                        {listing.brand}
                      </p>
                    )}
                    {listing.partNumber && (
                      <p className={`font-mono text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                        Part # {listing.partNumber}
                      </p>
                    )}
                  </div>
                </div>

                <div className={`border-t pt-4 ${isDark ? 'border-slate-700/60' : 'border-slate-100'}`}>
                  <PriceBreakdownCard pricing={listing.pricing} embedded />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div
                    className={`rounded-sm border p-4 ${
                      isDark
                        ? 'border-slate-700/60 bg-slate-800/40'
                        : 'border-slate-200 bg-slate-50'
                    }`}
                  >
                    <p
                      className={`text-[11px] font-bold uppercase tracking-[0.12em] ${
                        isDark ? 'text-slate-500' : 'text-slate-500'
                      }`}
                    >
                      Donor vehicle
                    </p>
                    <p
                      className={`mt-2 text-sm font-semibold leading-snug ${
                        isDark ? 'text-white' : 'text-slate-900'
                      }`}
                    >
                      {listing.donorVehicle?.label ||
                        [listing.donorVehicle?.make, listing.donorVehicle?.model, listing.donorVehicle?.year]
                          .filter(Boolean)
                          .join(' ')}
                    </p>
                    {listing.donorVehicle?.odometerKm != null && (
                      <p
                        className={`mt-2 flex items-center gap-2 text-sm ${
                          isDark ? 'text-slate-400' : 'text-slate-600'
                        }`}
                      >
                        <FaTachometerAlt className="shrink-0 opacity-60" />
                        {Number(listing.donorVehicle.odometerKm).toLocaleString('en-IN')} km
                      </p>
                    )}
                  </div>

                  <div
                    className={`flex items-center gap-3 rounded-sm border p-4 ${
                      isDark
                        ? 'border-emerald-800/40 bg-emerald-950/20'
                        : 'border-emerald-200 bg-emerald-50'
                    }`}
                  >
                    <FaShieldAlt
                      className={`shrink-0 text-lg ${isDark ? 'text-emerald-400' : 'text-[#388e3c]'}`}
                    />
                    <p className={`text-sm leading-relaxed ${isDark ? 'text-emerald-200' : 'text-emerald-800'}`}>
                      <strong>{listing.warrantyDays || gradeMeta.warrantyDays} days</strong> warranty
                      on this Grade {listing.grade} part
                    </p>
                  </div>
                </div>

                <div className={`border-t pt-4 ${isDark ? 'border-slate-700/60' : 'border-slate-100'}`}>
                  <h2 className={`mb-2 text-sm font-medium ${isDark ? 'text-slate-200' : 'text-slate-900'}`}>
                    Delivery &amp; services
                  </h2>
                  <SellerBlock
                    seller={listing.seller}
                    eta={listing.etaLabel || listing.eta}
                    warrantyMonths={null}
                    embedded
                  />
                </div>

                {listing.verificationPhoto?.url && (
                  <div className={`border-t pt-4 ${isDark ? 'border-slate-700/60' : 'border-slate-100'}`}>
                    <UsedVerificationPhotoSection
                      verificationPhoto={listing.verificationPhoto}
                      onPayNow={handlePayNowAfterPhoto}
                      paying={paying}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3 px-3 sm:px-0">
          <CrossSellSection items={crossSell} />
        </div>
      </div>

      <div
        className={`fixed inset-x-0 z-[110] border-t px-3 py-2.5 backdrop-blur-md md:hidden bottom-[calc(3.75rem+env(safe-area-inset-bottom,0px))] ${
          isDark ? 'border-slate-700/80 bg-[#0b1120]/95' : 'border-slate-200 bg-white/95'
        }`}
      >
        {listing.verificationPhoto?.url ? (
          <button
            type="button"
            onClick={handlePayNowAfterPhoto}
            disabled={paying}
            className={`${sc('pdpBuyNowBtn')} w-full`}
          >
            {paying ? 'Please wait…' : 'Pay now'}
          </button>
        ) : (
          <UsedCtaSection {...ctaProps} compact />
        )}
      </div>

      {showCheckModal && (
        <ConfirmModal
          title="Check Availability"
          body="Request sent to seller. They will physically check this part and send you a photo within 4 hours. You only pay after seeing the photo."
          confirmLabel="Send request"
          loading={checking}
          onConfirm={submitCheckAvailability}
          onCancel={() => setShowCheckModal(false)}
        />
      )}

      {showExpressModal && (
        <ConfirmModal
          title="Express Buy"
          body="You will be charged now. If the seller does not confirm stock within 30 minutes, you get a full automatic refund."
          confirmLabel="Confirm & checkout"
          loading={expressLoading}
          onConfirm={submitExpressBuy}
          onCancel={() => setShowExpressModal(false)}
        />
      )}
    </div>
  );
}
